import { TrackingApi } from '../apis';
import { ElementId } from '../types';
import { collectDomScopes, parseTrackData } from './collectDomScopes';
import { parseElementId } from './element-id';

const IMPRESSION_SELECTOR = '[data-track-id][data-track-impression]';

export function observeImpressions(
  container: HTMLElement,
  tracking: TrackingApi,
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  const tracked = new WeakSet<Element>();

  const intersectionObserver = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && !tracked.has(entry.target)) {
          tracked.add(entry.target);
          intersectionObserver.unobserve(entry.target);
          fireImpression(entry.target as HTMLElement, container, tracking);
        }
      }
    },
    { threshold: 0.5 },
  );

  function observe(el: Element) {
    if (!tracked.has(el)) {
      intersectionObserver.observe(el);
    }
  }

  function scanNode(node: Node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.matches(IMPRESSION_SELECTOR)) observe(el);
    el.querySelectorAll(IMPRESSION_SELECTOR).forEach(observe);
  }

  // Initial scan
  container.querySelectorAll(IMPRESSION_SELECTOR).forEach(observe);

  // Watch for dynamically added elements inside the container
  const containerObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scanNode);
    }
  });
  containerObserver.observe(container, { childList: true, subtree: true });

  // Watch for elements rendered outside the container (e.g. portals)
  const documentObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scanNode);
    }
  });
  documentObserver.observe(document.body, { childList: true, subtree: true });

  return () => {
    intersectionObserver.disconnect();
    containerObserver.disconnect();
    documentObserver.disconnect();
  };
}

function fireImpression(
  element: HTMLElement,
  container: HTMLElement,
  tracking: TrackingApi,
) {
  const trackId = element.dataset.trackId;
  if (!trackId) return;
  const elementSegment = parseElementId(trackId as ElementId);
  const elementData = parseTrackData(element);

  // If the element is inside the container, collect scopes in two parts:
  // inner (element → container) and outer (container → document.body).
  // For portal elements outside the container, walk all the way up.
  const insideContainer = container.contains(element);
  const domResult = insideContainer
    ? collectDomScopes(element, container)
    : collectDomScopes(element);
  const outerResult = insideContainer
    ? collectDomScopes(container)
    : { scopes: [], data: {} };
  const containerData = insideContainer ? parseTrackData(container) : {};

  const elementPath = [
    ...outerResult.scopes,
    ...domResult.scopes,
    elementSegment,
  ];
  const data = Object.assign(
    {},
    outerResult.data,
    containerData,
    domResult.data,
    elementData,
  );

  tracking.trackImpression({ elementPath, data });
}
